import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';

const CartContext = createContext();

// Cart actions
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART'
};

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM:
      const existingItem = state.items.find(item => item.productId === action.payload.productId);
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.productId === action.payload.productId
              ? { ...item, quantity: item.quantity + action.payload.quantity }
              : item
          )
        };
      } else {
        return {
          ...state,
          items: [...state.items, action.payload]
        };
      }

    case CART_ACTIONS.REMOVE_ITEM:
      return {
        ...state,
        items: state.items.filter(item => item.productId !== action.payload)
      };

    case CART_ACTIONS.UPDATE_QUANTITY:
      return {
        ...state,
        items: state.items.map(item =>
          item.productId === action.payload.productId
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: []
      };

    case CART_ACTIONS.LOAD_CART:
      return {
        ...state,
        items: action.payload || []
      };

    default:
      return state;
  }
};

// Function to get initial state from localStorage
const getInitialState = () => {
  try {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      const cartData = JSON.parse(savedCart);
      return { items: Array.isArray(cartData) ? cartData : [] };
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  return { items: [] };
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, getInitialState());
  const [isLoaded, setIsLoaded] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        if (Array.isArray(cartData)) {
          dispatch({ type: CART_ACTIONS.LOAD_CART, payload: cartData });
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save cart to localStorage whenever it changes (but not on initial load)
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('cart', JSON.stringify(state.items));
    }
  }, [state.items, isLoaded]);

  // Listen for storage changes across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'cart' && e.newValue !== null) {
        try {
          const cartData = JSON.parse(e.newValue);
          if (Array.isArray(cartData)) {
            dispatch({ type: CART_ACTIONS.LOAD_CART, payload: cartData });
          }
        } catch (error) {
          console.error('Error syncing cart from storage change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Ensure cart is saved before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem('cart', JSON.stringify(state.items));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.items]);

  // Cart actions
  const addToCart = (product, quantity = 1) => {
    const cartItem = {
      productId: product._id?.toString() || product._id,
      productName: product.title,
      price: product.price,
      currency: product.currency || 'USD', // Store currency with cart item
      quantity,
      image: product.assets && product.assets.length > 0 ? product.assets[0] : null,
      sellerId: product.userId._id?.toString() || product.userId._id,
      sellerName: product.userId.creatorProfile?.businessName || product.userId.displayName || product.userId.username,
      // Phase 6: PWYW and tip metadata
      pricingType: product.pricingType || 'fixed',
      minPrice: product.minPrice || 0,
      tipEnabled: product.tipEnabled || false,
    };

    dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: cartItem });
  };

  const removeFromCart = (productId) => {
    dispatch({ type: CART_ACTIONS.REMOVE_ITEM, payload: productId?.toString() || productId });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
    } else {
      dispatch({ type: CART_ACTIONS.UPDATE_QUANTITY, payload: { productId: productId?.toString() || productId, quantity } });
    }
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
    localStorage.removeItem('cart');
    // Also clear any pending order number if cart is cleared
    const pendingOrderNumber = sessionStorage.getItem('pendingOrderNumber');
    if (pendingOrderNumber) {
      // Try to clean up the pending order
      api.delete(`/orders/pending/${pendingOrderNumber}`).catch(err => {
        console.error('Error cleaning up pending order on cart clear:', err);
      });
      sessionStorage.removeItem('pendingOrderNumber');
    }
  };

  // Remove multiple items by product IDs
  const removeItems = (productIds) => {
    productIds.forEach(productId => {
      dispatch({ type: CART_ACTIONS.REMOVE_ITEM, payload: productId?.toString() || productId });
    });
  };

  // Remove items by seller ID
  const removeItemsBySeller = (sellerId) => {
    const itemsToRemove = state.items
      .filter(item => item.sellerId === sellerId)
      .map(item => item.productId?.toString() || item.productId);
    
    removeItems(itemsToRemove);
  };

  // Function to manually refresh cart from localStorage
  const refreshCart = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        if (Array.isArray(cartData)) {
          dispatch({ type: CART_ACTIONS.LOAD_CART, payload: cartData });
        }
      } catch (error) {
        console.error('Error refreshing cart from localStorage:', error);
      }
    }
  };

  // Computed values
  const totalItems = state.items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const isEmpty = state.items.length === 0;

  // Check if all items are from the same seller
  const isSingleSeller = () => {
    if (state.items.length === 0) return true;
    const sellerIds = [...new Set(state.items.map(item => item.sellerId))];
    return sellerIds.length === 1;
  };

  const getSellerInfo = () => {
    if (state.items.length === 0) return null;
    return {
      sellerId: state.items[0].sellerId,
      sellerName: state.items[0].sellerName
    };
  };

  // Group items by seller
  const getItemsBySeller = () => {
    if (state.items.length === 0) return {};
    
    return state.items.reduce((groups, item) => {
      const sellerId = item.sellerId;
      if (!groups[sellerId]) {
        groups[sellerId] = {
          sellerId,
          sellerName: item.sellerName,
          items: [],
          totalItems: 0,
          totalPrice: 0
        };
      }
      groups[sellerId].items.push(item);
      groups[sellerId].totalItems += item.quantity;
      groups[sellerId].totalPrice += item.price * item.quantity;
      return groups;
    }, {});
  };

  // Get seller groups as array
  const getSellerGroups = () => {
    const groups = getItemsBySeller();
    return Object.values(groups);
  };

  const value = {
    items: state.items,
    totalItems,
    totalPrice,
    isEmpty,
    isSingleSeller: isSingleSeller(),
    sellerInfo: getSellerInfo(),
    getItemsBySeller,
    getSellerGroups,
    addToCart,
    removeFromCart,
    removeItems,
    removeItemsBySeller,
    updateQuantity,
    clearCart,
    refreshCart,
    isLoaded
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
